import type { ComponentPropsWithRef } from "react";
import { Fragment, forwardRef } from "react";
import { omit } from "lodash";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { useAttachmentMetadata } from "@circle-react-shared/BuilderUI/Media/AttachmentContext";
import type { StyleValue } from "@circle-react-shared/BuilderUI/StyleValue.types";
import { parseSizing } from "@circle-react-shared/BuilderUI/utils/parseSizing";
import { parseStyles } from "@circle-react-shared/BuilderUI/utils/parseStyles";
import { defaultStyles } from "./defaultStyles";
import defaultPlaceholderImage from "./img_preview_large.png";

interface ImageProps extends Omit<ComponentPropsWithRef<"img">, "loading"> {
  alt?: string;
  "data-preview-mode"?: boolean;
  href?: string;
  image: {
    signed_id: string;
    placeholder?: string;
  };
  loading?: "lazy" | "eager";
  styles?: Record<string, StyleValue>;
  target?: string;
}

export const Img = forwardRef<HTMLImageElement, ImageProps>(
  (
    {
      image,
      styles = {},
      className,
      alt = "",
      loading = "lazy",
      href = "",
      target = "_self",
      "data-preview-mode": isPreviewMode = false,
      ...rest
    },
    ref,
  ) => {
    const inlineStyles = {
      ...parseStyles({
        styles: omit(styles, "sizing"),
        defaultStyles,
      }),
      ...parseSizing({
        sizing: styles.sizing,
        defaultSizing: defaultStyles.sizing,
      }),
    };

    const attachmentMetadata = useAttachmentMetadata(image.signed_id);
    const { url: imageUrl } = attachmentMetadata ?? {};
    const imageSrc = imageUrl || image.placeholder || defaultPlaceholderImage;

    const Wrapper = href ? "a" : Fragment;
    const wrapperProps =
      href && !isPreviewMode
        ? {
            href,
            target,
            rel: target === "_blank" ? "noopener noreferrer" : undefined,
          }
        : {};

    return (
      <Wrapper {...wrapperProps}>
        <img
          {...rest}
          ref={ref}
          src={imageSrc}
          alt={alt}
          className={classNames(
            "wb-h-auto wb-max-h-96 wb-w-full wb-max-w-full wb-object-cover",
            className,
          )}
          style={inlineStyles}
          loading={loading}
        />
      </Wrapper>
    );
  },
);

Img.displayName = "Img";
